import { useEffect, useId, useState } from 'react';
import { ClipboardList, Inbox, LockKeyhole, PencilLine } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '../components/CoachModal';
import { DraftInbox } from '@/features/draft-inbox/DraftInbox';
import { draftContextKey, useComposerDraftContext } from '@/features/coach-review/composerDrafts';
import type { PlayerGoal } from '@/lib/types';
import { AssistWorkspace } from './CoachAssistPanel';
import './assist-sheet.css';

type AssistTask = 'capture' | 'prepare' | 'drafts';
export type CoachAssistSheetProps = {
  open: boolean;
  onClose: () => void;
  onManualFeedback: () => void;
  captureId?: string | null;
  returnFocusTo?: HTMLElement | null;
  onOpenPlayer?: (playerId: string, name: string) => void;
  onNewGoal?: (playerId: string) => void;
  onEditGoal?: (goal: PlayerGoal) => void;
};

/** First open mounts the owned workspace; ordinary close only hides it. */
export function CoachAssistSheet(props: CoachAssistSheetProps) {
  const owner = useComposerDraftContext();
  const [opened, setOpened] = useState(props.open);
  useEffect(() => { if (props.open) setOpened(true); }, [props.open]);
  if (!opened || !owner) return null;
  return <OwnedAssistSheet key={draftContextKey(owner)} {...props} owner={owner} />;
}

function OwnedAssistSheet({ open, onClose, onManualFeedback, captureId, returnFocusTo, owner, ...callbacks }: CoachAssistSheetProps & { owner: NonNullable<ReturnType<typeof useComposerDraftContext>> }) {
  const id = useId();
  const [task, setTask] = useState<AssistTask>(captureId ? 'drafts' : 'capture');
  const [requestedCapture, setRequestedCapture] = useState(captureId ?? null);
  const [selectionIntent, setSelectionIntent] = useState(0);
  useEffect(() => {
    if (captureId) { setRequestedCapture(captureId); setTask('drafts'); }
  }, [captureId]);
  const tabs = [
    { key: 'capture', label: 'Capture', icon: PencilLine },
    { key: 'prepare', label: 'Prepare', icon: ClipboardList },
    { key: 'drafts', label: 'Private drafts', icon: Inbox },
  ] as const;
  return <CoachModal title="Tktic Assist" description="Optional coaching support. Your work stays private until you review and approve it." size="sheet" active={open} onClose={onClose} returnFocusTo={returnFocusTo} bodyClassName="assist-sheet-body" data-testid="coach-assist-sheet" footer={<div className="flex flex-wrap items-center justify-between gap-2"><span className="flex items-center gap-2 text-xs text-ink-muted"><LockKeyhole aria-hidden="true" className="size-4" />Nothing is shared automatically</span><Button type="button" variant="outline" onClick={onClose}>Back to Board</Button></div>}>
    <div className="assist-task-tabs" role="tablist" aria-label="Tktic Assist tasks">
      {tabs.map(({ key, label, icon: Icon }, index) => <button key={key} type="button" role="tab" id={`${id}-${key}-tab`} aria-controls={`${id}-${key}-panel`} aria-selected={task === key} tabIndex={task === key ? 0 : -1} onClick={() => setTask(key)} onKeyDown={event => {
        const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
        if (next === null) return;
        event.preventDefault(); setTask(tabs[next].key); document.getElementById(`${id}-${tabs[next].key}-tab`)?.focus();
      }}><Icon aria-hidden="true" className="size-4" />{label}</button>)}
    </div>
    <div className="assist-sheet-task" hidden={task === 'drafts'} id={`${id}-${task === 'prepare' ? 'prepare' : 'capture'}-panel`} role="tabpanel" aria-labelledby={`${id}-${task === 'prepare' ? 'prepare' : 'capture'}-tab`}>
      <h3 className="font-display text-lg text-ink">{task === 'prepare' ? 'Go in with a clear focus.' : 'Keep the moment. Find the words later.'}</h3>
      <p className="mt-1 text-sm text-ink-muted">{task === 'prepare' ? 'Use saved coaching history to prepare for a session or conversation.' : 'Capture what you noticed while it is fresh. No polished feedback needed.'}</p>
      <AssistWorkspace owner={owner} visible={open && task !== 'drafts'} task={task === 'prepare' ? 'prepare' : 'capture'} {...callbacks} onReviewCapture={id => { setRequestedCapture(id); setSelectionIntent(previous => previous + 1); setTask('drafts'); }} />
    </div>
    <div hidden={task !== 'drafts'} id={`${id}-drafts-panel`} role="tabpanel" aria-labelledby={`${id}-drafts-tab`} className="assist-sheet-task">
      <h3 className="font-display text-lg text-ink">Review before you share.</h3>
      <p className="mt-1 mb-4 text-sm text-ink-muted">Confirm what happened, shape the message, then approve it.</p>
      <DraftInbox embedded visible={open && task === 'drafts'} captureId={requestedCapture} selectionIntent={selectionIntent} onManualFeedback={onManualFeedback} />
    </div>
  </CoachModal>;
}
